import { useState } from "react";
import type { ReactNode } from "react";
import { getCustomerPortalUrl, getMyB2bGrantInfo, getMyMentors, updateStudentPermissions, revokeRelationAsStudent, acceptInvitationByToken, declineInvitationByToken, useQuery, useAction } from "wasp/client/operations";
import PlanSelectionModal from "../dashboard/PlanSelectionModal";
import { Link as WaspRouterLink, routes } from "wasp/client/router";
import type { User } from "wasp/entities";
import { Button } from "../client/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../client/components/ui/card";
import { Separator } from "../client/components/ui/separator";
import { Badge } from "../client/components/ui/badge";
import { Input } from "../client/components/ui/input";
import { Textarea } from "../client/components/ui/textarea";
import { Label } from "../client/components/ui/label";
import {
  PaymentPlanId,
  SubscriptionStatus,
  canUpgrade,
  parsePaymentPlanId,
  prettyPaymentPlanName,
} from "../payment/plans";
import { toast } from "../client/hooks/use-toast";
import { Edit2, Save, X, Twitter, Instagram, Mail, ShieldCheck } from "lucide-react";
import { updateUserProfile } from "wasp/client/operations";
import useTranslation from "../client/hooks/useTranslation";
import type { Translations } from "../client/i18n/translations";
import SeedDemoDataButton from "../client/components/SeedDemoDataButton";
import SeedForexAnalysesButton from "../client/components/SeedForexAnalysesButton";
import { Switch } from "../client/components/ui/switch";
import { format } from "date-fns";
import { DisplayModeCard } from "../lite/settings/LiteSettingsCards";

function MentorsTutorsSection() {
  const t = useTranslation();

  const { data, refetch } = useQuery(getMyMentors);
  const updatePermsFn = useAction(updateStudentPermissions);
  const revokeFn = useAction(revokeRelationAsStudent);
  const acceptFn = useAction(acceptInvitationByToken);
  const declineFn = useAction(declineInvitationByToken);

  const mentorsData = data as { relations: any[]; pendingInvitations: any[] } | undefined;
  const relations = mentorsData?.relations ?? [];
  const pendingInvitations = mentorsData?.pendingInvitations ?? [];

  type PermsType = {
    canViewJournal: boolean;
    canViewDashboard: boolean;
    canViewAnalytics: boolean;
    canViewSessions: boolean;
    canViewPropFirmManager: boolean;
  };

  const PERM_KEYS: Array<{ key: keyof PermsType; label: string }> = [
    { key: "canViewJournal", label: t.mentor.canViewJournal },
    { key: "canViewDashboard", label: t.mentor.canViewDashboard },
    { key: "canViewAnalytics", label: t.mentor.canViewAnalytics },
    { key: "canViewSessions", label: t.mentor.canViewSessions },
    { key: "canViewPropFirmManager", label: t.mentor.canViewPropFirmManager },
  ];

  const handlePermToggle = async (
    currentPerms: PermsType,
    key: keyof PermsType,
    value: boolean,
  ) => {
    const DASHBOARD_DEPS: Array<keyof PermsType> = ["canViewAnalytics", "canViewPropFirmManager", "canViewSessions"];

    let updated = { ...currentPerms, [key]: value };

    // Enabling dashboard → enable its dependencies
    if (key === "canViewDashboard" && value) {
      DASHBOARD_DEPS.forEach((dep) => { updated[dep] = true; });
    }
    // Disabling a dashboard dependency → disable dashboard too
    if (DASHBOARD_DEPS.includes(key) && !value) {
      updated.canViewDashboard = false;
    }

    try {
      await updatePermsFn(updated);
      refetch();
    } catch (err: unknown) {
      toast({
        title: "Erreur",
        description: err instanceof Error ? err.message : "Erreur",
        variant: "destructive",
      });
    }
  };

  const handleRevoke = async (relationId: string) => {
    if (!window.confirm(t.mentor.revokeMentorConfirm)) return;
    try {
      await revokeFn({ relationId });
      refetch();
      toast({ title: "Tuteur révoqué", description: "La relation de tutorat a été supprimée." });
    } catch (err: unknown) {
      toast({
        title: "Erreur",
        description: err instanceof Error ? err.message : "Erreur",
        variant: "destructive",
      });
    }
  };

  const handleAcceptInvitation = async (token: string) => {
    try {
      await acceptFn({ token });
      refetch();
      toast({ title: "Invitation acceptée !" });
    } catch (err: unknown) {
      toast({
        title: "Erreur",
        description: err instanceof Error ? err.message : "Erreur",
        variant: "destructive",
      });
    }
  };

  const handleDeclineInvitation = async (token: string) => {
    try {
      await declineFn({ token });
      refetch();
      toast({ title: "Invitation refusée." });
    } catch (err: unknown) {
      toast({
        title: "Erreur",
        description: err instanceof Error ? err.message : "Erreur",
        variant: "destructive",
      });
    }
  };

  return (
    <Card className="mb-4 lg:m-8">
      <CardHeader>
        <CardTitle className="text-foreground text-base font-semibold leading-6 flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-primary" />
          {t.mentor.mentorsSection}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {relations.length === 0 ? (
          <div className="px-6 py-4">
            <p className="text-sm text-muted-foreground">{t.mentor.noMentors}</p>
          </div>
        ) : (
          relations.map((rel: any) => {
            const mentorName =
              [rel.mentor?.firstName, rel.mentor?.lastName].filter(Boolean).join(" ") || "Tuteur";
            const perms: PermsType = rel.permissions ?? {
              canViewJournal: true,
              canViewDashboard: true,
              canViewAnalytics: true,
              canViewSessions: true,
              canViewPropFirmManager: true,
            };

            return (
              <div key={rel.id}>
                <Separator />
                <div className="px-6 py-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-foreground">{mentorName}</p>
                      <p className="text-xs text-muted-foreground">
                        {t.mentor.activeSince} {rel.createdAt ? format(new Date(rel.createdAt), "dd/MM/yyyy") : "—"}
                      </p>
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      className="h-7 text-xs px-3"
                      onClick={() => handleRevoke(rel.id)}
                    >
                      {t.mentor.revokeMentor}
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">{t.mentor.permissionsTitle}</p>
                    {PERM_KEYS.map(({ key, label }) => (
                      <div key={key} className="flex items-center justify-between">
                        <span className="text-sm text-foreground">{label}</span>
                        <Switch
                          checked={perms[key] ?? false}
                          onCheckedChange={(val) => handlePermToggle(perms, key, val)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })
        )}

        {pendingInvitations.length > 0 && (
          <>
            <Separator />
            <div className="px-6 py-4 space-y-3">
              <p className="text-xs font-medium text-muted-foreground">{t.mentor.pendingInvitations}</p>
              {pendingInvitations.map((inv: any) => {
                const mentorName =
                  [inv.mentor?.firstName, inv.mentor?.lastName].filter(Boolean).join(" ") || "Tuteur";
                return (
                  <div key={inv.id} className="flex items-center justify-between gap-3">
                    <p className="text-sm text-foreground">{mentorName}</p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="h-7 text-xs px-3 bg-primary hover:bg-primary/90 text-primary-foreground"
                        onClick={() => handleAcceptInvitation(inv.token)}
                      >
                        {t.mentor.acceptInvitation}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs px-3"
                        onClick={() => handleDeclineInvitation(inv.token)}
                      >
                        {t.mentor.declineInvitation}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function AccountPage({ user: initialUser }: { user: User }) {
  const t = useTranslation();
  const [user, setUser] = useState(initialUser);
  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [isEditingAbout, setIsEditingAbout] = useState(false);
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [username, setUsername] = useState(user.username || "");
  const [about, setAbout] = useState(user.about || "");
  const [hideWeekend, setHideWeekend] = useState(
    () => localStorage.getItem("calendar_hide_weekend") === "true"
  );
  const [showErrors, setShowErrors] = useState(
    () => localStorage.getItem("calendar_show_errors") === "true"
  );
  const [hideInactive, setHideInactive] = useState(
    () => localStorage.getItem("accounts.hideInactive") === "true"
  );

  const updateUserProfileFn = useAction(updateUserProfile);

  const [chargeReminderEmails, setChargeReminderEmails] = useState<boolean>(
    initialUser.chargeReminderEmails ?? true,
  );
  const toggleChargeReminders = async (value: boolean) => {
    setChargeReminderEmails(value);
    try {
      await updateUserProfileFn({ chargeReminderEmails: value });
    } catch (error: any) {
      setChargeReminderEmails(!value);
      toast({
        title: t.common.error,
        description: error?.message || t.account.updateError,
        variant: "destructive",
      });
    }
  };

  const handleSaveUsername = async () => {
    try {
      const updatedUser = await updateUserProfileFn({ username });
      setUser(updatedUser);
      setIsEditingUsername(false);
      toast({
        title: t.account.profileUpdated,
        description: t.account.profileUpdated,
      });
    } catch (error: any) {
      toast({
        title: t.common.error,
        description: error.message || t.account.updateError,
        variant: "destructive",
      });
    }
  };

  const handleSaveAbout = async () => {
    try {
      const updatedUser = await updateUserProfileFn({ about });
      setUser(updatedUser);
      setIsEditingAbout(false);
      toast({
        title: t.account.profileUpdated,
        description: t.account.profileUpdated,
      });
    } catch (error: any) {
      toast({
        title: t.common.error,
        description: error.message || t.account.updateError,
        variant: "destructive",
      });
    }
  };

  return (
    <div className="mt-10 px-6">
      <Card className="mb-4 lg:m-8">
        <CardHeader>
          <CardTitle className="text-foreground text-base font-semibold leading-6">
            {t.account.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="space-y-0">
            {!!user.email && (
              <div className="px-6 py-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 sm:gap-4">
                  <div className="text-muted-foreground text-sm font-medium">
                    {t.account.email}
                  </div>
                  <div className="text-foreground mt-1 text-sm sm:col-span-2 sm:mt-0">
                    {user.email}
                  </div>
                </div>
              </div>
            )}
            <Separator />
            <div className="px-6 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 sm:gap-4">
                <div className="text-muted-foreground text-sm font-medium">
                  {t.account.username}
                </div>
                <div className="text-foreground mt-1 text-sm sm:col-span-2 sm:mt-0">
                  {isEditingUsername ? (
                    <div className="flex items-center gap-2">
                      <Input
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        className="flex-1"
                        placeholder="Nom d'utilisateur"
                      />
                      <Button
                        size="sm"
                        onClick={handleSaveUsername}
                        className="h-8"
                      >
                        <Save className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setIsEditingUsername(false);
                          setUsername(user.username || "");
                        }}
                        className="h-8"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <span>{user.username || t.account.notDefined}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setIsEditingUsername(true)}
                        className="h-8"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <Separator />
            <div className="px-6 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 sm:gap-4">
                <div className="text-muted-foreground text-sm font-medium">
                  {t.account.plan}
                </div>
                <UserCurrentSubscriptionPlan
                  subscriptionPlan={user.subscriptionPlan}
                  subscriptionStatus={user.subscriptionStatus}
                  subscriptionPeriodEnd={user.subscriptionPeriodEnd}
                  b2bGrantId={user.b2bGrantId}
                />
              </div>
            </div>
            <Separator />
            <div className="px-6 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 sm:gap-4">
                <div className="text-muted-foreground text-sm font-medium">
                  {t.account.about}
                </div>
                <div className="text-foreground mt-1 text-sm sm:col-span-2 sm:mt-0">
                  {isEditingAbout ? (
                    <div className="space-y-2">
                      <Textarea
                        value={about}
                        onChange={(e) => setAbout(e.target.value)}
                        placeholder="Décrivez-vous..."
                        rows={3}
                      />
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={handleSaveAbout}
                        >
                          <Save className="mr-2 h-4 w-4" />
                          Enregistrer
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setIsEditingAbout(false);
                            setAbout(user.about || "");
                          }}
                        >
                          <X className="mr-2 h-4 w-4" />
                          Annuler
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between">
                      <span className="flex-1">{user.about || t.account.noDescription}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setIsEditingAbout(true)}
                        className="h-8 ml-2"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-4 lg:m-8">
        <CardHeader>
          <CardTitle className="text-foreground text-base font-semibold leading-6">
            {t.tradingAccounts.chargeRemindersTitle}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm text-foreground">{t.tradingAccounts.chargeRemindersLabel}</p>
              <p className="text-xs text-muted-foreground">{t.tradingAccounts.chargeRemindersHint}</p>
            </div>
            <Switch checked={chargeReminderEmails} onCheckedChange={toggleChargeReminders} />
          </div>
        </CardContent>
      </Card>

      <DisplayModeCard onUpgrade={() => setIsPlanModalOpen(true)} />
      <PlanSelectionModal open={isPlanModalOpen} onOpenChange={setIsPlanModalOpen} />

      {/* Preferences */}
      <Card className="mb-4 lg:m-8">
        <CardHeader>
          <CardTitle className="text-foreground text-base font-semibold leading-6">
            {t.account.preferences}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="space-y-0">
            <div className="px-6 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 sm:gap-4 items-center">
                <div className="text-muted-foreground text-sm font-medium">
                  {t.account.hideWeekend}
                </div>
                <div className="mt-1 sm:mt-0 sm:col-span-2 flex justify-start sm:justify-end">
                  <Switch
                    checked={hideWeekend}
                    onCheckedChange={(next) => {
                      setHideWeekend(next);
                      localStorage.setItem("calendar_hide_weekend", String(next));
                    }}
                  />
                </div>
              </div>
            </div>
            <Separator />
            <div className="px-6 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 sm:gap-4 items-center">
                <div className="text-muted-foreground text-sm font-medium">
                  {t.account.showCalendarErrors}
                </div>
                <div className="mt-1 sm:mt-0 sm:col-span-2 flex justify-start sm:justify-end">
                  <Switch
                    checked={showErrors}
                    onCheckedChange={(next) => {
                      setShowErrors(next);
                      localStorage.setItem("calendar_show_errors", String(next));
                    }}
                  />
                </div>
              </div>
            </div>
            <Separator />
            <div className="px-6 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 sm:gap-4 items-center">
                <div className="text-muted-foreground text-sm font-medium">
                  {t.account.hideInactiveAccounts}
                </div>
                <div className="mt-1 sm:mt-0 sm:col-span-2 flex justify-start sm:justify-end">
                  <Switch
                    checked={hideInactive}
                    onCheckedChange={(next) => {
                      setHideInactive(next);
                      localStorage.setItem("accounts.hideInactive", String(next));
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Support & Community */}
      <Card className="mb-4 lg:m-8">
        <CardHeader>
          <CardTitle className="text-foreground text-base font-semibold leading-6">
            {t.landing.footer.community}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="space-y-0">
            <div className="px-6 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 sm:gap-4 items-center">
                <div className="text-muted-foreground text-sm font-medium">
                  {t.landing.footer.contactSupport}
                </div>
                <div className="mt-1 sm:mt-0 sm:col-span-2">
                  <a
                    href="mailto:contact@edgely.trade"
                    className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors"
                  >
                    <Mail className="h-4 w-4" />
                    contact@edgely.trade
                  </a>
                </div>
              </div>
            </div>
            <Separator />
            <div className="px-6 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 sm:gap-4 items-center">
                <div className="text-muted-foreground text-sm font-medium">
                  {t.landing.footer.followUs}
                </div>
                <div className="mt-1 sm:mt-0 sm:col-span-2">
                  <a
                    href="https://x.com/edgely_trade"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors"
                  >
                    <Twitter className="h-4 w-4" />
                    @edgely_trade
                  </a>
                </div>
              </div>
            </div>
            <Separator />
            <div className="px-6 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 sm:gap-4 items-center">
                <div className="text-muted-foreground text-sm font-medium">
                  {t.landing.footer.followInstagram}
                </div>
                <div className="mt-1 sm:mt-0 sm:col-span-2">
                  <a
                    href="https://www.instagram.com/_edgely_"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors"
                  >
                    <Instagram className="h-4 w-4" />
                    @_edgely_
                  </a>
                </div>
              </div>
            </div>
            <Separator />
            <div className="px-6 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 sm:gap-4 items-center">
                <div className="text-muted-foreground text-sm font-medium">
                  {t.landing.footer.joinDiscord}
                </div>
                <div className="mt-1 sm:mt-0 sm:col-span-2">
                  <a
                    href="https://discord.gg/jnrfKHzUVp"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors"
                  >
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057c.002.022.015.043.03.056a19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                    </svg>
                    Discord
                  </a>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <MentorsTutorsSection />

      {/* Development Section - Only show in development */}
      {process.env.NODE_ENV === "development" && (
        <Card className="mb-4 lg:m-8">
          <CardHeader>
            <CardTitle className="text-foreground text-base font-semibold leading-6">
              Développement
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div>
              <p className="text-sm text-muted-foreground mb-2">Generate demo data (routines, goals, analyses)</p>
              <SeedDemoDataButton />
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-2">Generate forex fundamental analyses only</p>
              <SeedForexAnalysesButton />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function daysUntil(date: Date): number {
  const now = new Date();
  const diff = date.getTime() - now.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function formatRelativeDays(days: number, t: Translations): string {
  if (days === 0) return t.account.today;
  if (days === 1) return t.account.tomorrow;
  return t.account.inDays.replace("{days}", String(days));
}

function UserCurrentSubscriptionPlan({
  subscriptionPlan,
  subscriptionStatus,
  subscriptionPeriodEnd,
  b2bGrantId,
}: Pick<User, "subscriptionPlan" | "subscriptionStatus" | "subscriptionPeriodEnd" | "b2bGrantId">) {
  const t = useTranslation();
  const [showPlanModal, setShowPlanModal] = useState(false);
  // Membre B2B : Max offert par un partenaire. La query ne part que pour eux.
  const isB2bMember = !!b2bGrantId;
  const { data: b2bGrantInfo } = useQuery(getMyB2bGrantInfo, undefined, {
    enabled: isB2bMember,
  });

  const hasActivePlan =
    subscriptionPlan !== null &&
    subscriptionPlan !== "free" &&
    subscriptionStatus !== null &&
    subscriptionStatus !== SubscriptionStatus.Deleted;

  const periodEnd = subscriptionPeriodEnd ? new Date(subscriptionPeriodEnd) : null;
  const daysLeft = periodEnd ? daysUntil(periodEnd) : null;
  const showDays = daysLeft !== null && daysLeft >= 0 && daysLeft < 365;
  const narrowedDaysLeft = daysLeft as number;

  let badge: ReactNode = null;
  let dateLine: ReactNode = null;

  if (subscriptionStatus === SubscriptionStatus.Active && hasActivePlan) {
    badge = <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/20">{t.account.statusActive}</Badge>;
    // Membre B2B : pas de renouvellement Stripe, une date résiduelle mentirait.
    if (periodEnd && showDays && !isB2bMember) {
      dateLine = (
        <p className="text-xs text-muted-foreground mt-1">
          {t.account.renewalOn} {periodEnd.toLocaleDateString()} · {formatRelativeDays(narrowedDaysLeft, t)}
        </p>
      );
    }
  } else if (subscriptionStatus === SubscriptionStatus.CancelAtPeriodEnd) {
    badge = <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 hover:bg-orange-500/20">{t.account.statusCancelAtPeriodEnd}</Badge>;
    if (periodEnd && showDays) {
      dateLine = (
        <p className="text-xs text-muted-foreground mt-1">
          {t.account.expiresOn} {periodEnd.toLocaleDateString()} · {formatRelativeDays(narrowedDaysLeft, t)}
        </p>
      );
    }
  } else if (subscriptionStatus === SubscriptionStatus.PastDue) {
    badge = <Badge className="bg-red-500/20 text-red-400 border-red-500/30 hover:bg-red-500/20">{t.account.statusPastDue}</Badge>;
    dateLine = (
      <p className="text-xs text-red-400 mt-1">{t.account.pastDueWarning}</p>
    );
  } else if (subscriptionStatus === SubscriptionStatus.Deleted) {
    badge = <Badge className="bg-slate-500/20 text-slate-400 border-slate-500/30 hover:bg-slate-500/20">{t.account.statusDeleted}</Badge>;
  }

  const planName = hasActivePlan && subscriptionPlan
    ? prettyPaymentPlanName(parsePaymentPlanId(subscriptionPlan))
    : t.account.freePlan;

  return (
    <div className="mt-1 sm:col-span-2 sm:mt-0 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="text-foreground text-sm">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium">{planName}</span>
          {badge}
          {isB2bMember && b2bGrantInfo && (
            <Badge variant="secondary">{t.b2bMember.offeredBadge}</Badge>
          )}
        </div>
        {isB2bMember && b2bGrantInfo && (
          <p className="text-xs text-muted-foreground mt-1">
            {t.b2bMember.offeredBy.replace("{partner}", b2bGrantInfo.partnerName)}
          </p>
        )}
        {dateLine}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {!isB2bMember && canUpgrade(subscriptionPlan) && (
          <Button
            variant="default"
            size="sm"
            onClick={() => setShowPlanModal(true)}
          >
            {t.nav.upgradeMyPlan}
          </Button>
        )}
        {/* Membre B2B : un ancien customer Stripe ouvrirait un portail sans abonnement. */}
        {!isB2bMember && <CustomerPortalButton />}
      </div>
      <PlanSelectionModal open={showPlanModal} onOpenChange={setShowPlanModal} />
    </div>
  );
}

function CustomerPortalButton() {
  const { data: customerPortalUrl, isLoading: isCustomerPortalUrlLoading, error } =
    useQuery(getCustomerPortalUrl, undefined, {
      retry: false,
      onError: () => {
        // Silently handle error - user may not have a subscription
      },
    });

  if (!customerPortalUrl || error) {
    return null;
  }

  return (
    <a href={customerPortalUrl} target="_blank" rel="noopener noreferrer">
      <Button disabled={isCustomerPortalUrlLoading} variant="link">
        Manage Payment Details
      </Button>
    </a>
  );
}

